import React, { useState, useCallback, useEffect, useRef } from 'react';
import PickerModal from '../Form2/PickerModal';
import { MEASURES_HANDBOOK_ID, orderMeasures, priorityCount } from './measureOrder';
import { parseDecimal, toDecimalPoint } from '../../../utils/decimalUtils';
import DecimalInput from '../../fields/DecimalInput';

const RecommendedMeasuresSection = ({
  recommendedMeasures,
  handbooks,
  onAddRow,
  onDeleteRow,
  onUpdateField,
  form1Data,
  dataType,
  selectedForm45
}) => {
  // Состояние для picker
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [activeItemId, setActiveItemId] = useState(null);

  // Получаем справочник с ID 25
  const handbook25 = handbooks.find(h => h.id === 25);
  const vidOptions = handbook25 && handbook25.references ? handbook25.references : [];

  // Функция для открытия picker
  const openPicker = useCallback((fieldName, handbookId, itemId) => {
    
    const handbook = handbooks.find(h => h.id === handbookId);
    
    if (handbook && handbook.references) {
      // В списке мероприятий частые стоят первыми — см. measureOrder.js
      const isMeasures = handbookId === MEASURES_HANDBOOK_ID;
      const references = isMeasures ? orderMeasures(handbook.references) : handbook.references;
      const priorityLength = isMeasures ? priorityCount(handbook.references) : 0;
      const options = references.map((r, index) => ({
        value: r.id.toString(),
        label: r.name,
        description: r.description || null,
        priority: index < priorityLength
      }));
      
      if (options && options.length > 0) {
        let currentValue = '';
        
        if (itemId) {
          const item = recommendedMeasures.find(m => m.id === itemId);
          const rawValue = item ? item[fieldName] : null;
          // Преобразуем значение в строку для правильного сравнения
          currentValue = rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
          setActiveItemId(itemId);
        } else {
          setActiveItemId(null);
        }
        
        const selectedIndex = options.findIndex(opt => opt.value === currentValue);
        
        setPickerField(fieldName);
        setPickerOptions(options);
        setPickerSearch('');
        setSelectedOptionIndex(selectedIndex);
        setShowPicker(true);
      }
    }
  }, [handbooks, recommendedMeasures]);

  // Функция для закрытия picker
  const closePicker = useCallback(() => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerSearch('');
    setSelectedOptionIndex(-1);
    setActiveItemId(null);
  }, []);

  // Функция для получения отфильтрованных опций
  const getFilteredOptions = useCallback(() => {
    if (!pickerSearch) return pickerOptions;
    const searchLower = pickerSearch.toLowerCase();
    return pickerOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchLower) ||
      (opt.description && opt.description.toLowerCase().includes(searchLower))
    );
  }, [pickerOptions, pickerSearch]);

  // Функция для получения индекса выбранного элемента в отфильтрованном списке
  const getSelectedIndexInFiltered = useCallback(() => {
    const filtered = getFilteredOptions();
    if (selectedOptionIndex < 0 || selectedOptionIndex >= pickerOptions.length) return -1;
    const selectedValue = pickerOptions[selectedOptionIndex].value;
    return filtered.findIndex(opt => opt.value === selectedValue);
  }, [getFilteredOptions, pickerOptions, selectedOptionIndex]);

  // Функция для выбора значения из picker
  // Используем ref для хранения activeItemId, чтобы избежать проблем с замыканием
  const activeItemIdRef = useRef(null);
  
  // Обновляем ref при изменении activeItemId
  useEffect(() => {
    activeItemIdRef.current = activeItemId;
  }, [activeItemId]);
  
  const selectPickerValue = useCallback((value) => {
    
    // Используем ref вместо state для получения актуального значения
    const currentItemId = activeItemIdRef.current;
    
    if (currentItemId) {
      onUpdateField(currentItemId, pickerField, value);
    }
    closePicker();
  }, [pickerField, onUpdateField, closePicker, activeItemId]);

  // Получаем текущее значение picker
  const getPickerValue = useCallback(() => {
    if (activeItemId) {
      const item = recommendedMeasures.find(m => m.id === activeItemId);
      return item ? item[pickerField] || '' : '';
    }
    return '';
  }, [activeItemId, pickerField, recommendedMeasures]);

  // Обработка клавиатуры для picker
  useEffect(() => {
    if (!showPicker) return;

    const handleKeyDown = (e) => {
      const filtered = getFilteredOptions();
      let newIndex = getSelectedIndexInFiltered();

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        newIndex = newIndex < filtered.length - 1 ? newIndex + 1 : 0;
        const newValue = filtered[newIndex].value;
        const originalIndex = pickerOptions.findIndex(opt => opt.value === newValue);
        setSelectedOptionIndex(originalIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        newIndex = newIndex > 0 ? newIndex - 1 : filtered.length - 1;
        const newValue = filtered[newIndex].value;
        const originalIndex = pickerOptions.findIndex(opt => opt.value === newValue);
        setSelectedOptionIndex(originalIndex);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (newIndex >= 0 && newIndex < filtered.length) {
          selectPickerValue(filtered[newIndex].value);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closePicker();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPicker, getFilteredOptions, getSelectedIndexInFiltered, selectPickerValue, closePicker, pickerOptions]);

  // Функция для получения максимальной площади выдела из формы №1
  const getMaxPloshad = () => {
    if (!form1Data) return 0;
    
    // Для формы №4
    if (selectedForm45 === 4) {
      if (dataType === 'to') {
        // Для материалов ТО используем площадь лесотаксационного выдела
        return parseDecimal(form1Data.sga) || 0;
      } else {
        // Для фактических данных используем площадь лесопатологического выдела
        return parseDecimal(form1Data.lp_sga) || parseDecimal(form1Data.slp) || 0;
      }
    }
    // Для формы №5
    else if (selectedForm45 === 5) {
      if (dataType === 'to') {
        // Для материалов ТО используем площадь лесотаксационного выдела
        return parseDecimal(form1Data.sga) || 0;
      } else {
        // Для фактических данных используем площадь лесопатологического выдела
        return parseDecimal(form1Data.slp) || 0;
      }
    }
    
    return 0;
  };

  // Функция для расчета суммы площадей всех мероприятий (кроме текущего)
  const getTotalPloshadExcluding = (excludeId) => {
    return recommendedMeasures
      .filter(item => item.id !== excludeId && item.srekme)
      .reduce((sum, item) => {
        const value = parseDecimal(item.srekme) || 0;
        return sum + value;
      }, 0);
  };

  // Функция для валидации площади
  const validatePloshad = (value) => {
    if (!value) return true; // Пустое значение допустимо
    
    // Проверяем формат числа
    const regex = /^\d{0,8}(\.\d{0,4})?$/;
    if (!regex.test(value)) return false;
    
    const num = parseDecimal(value);
    if (isNaN(num)) return false;
    
    // Проверяем количество цифр до запятой
    const parts = value.split('.');
    const integerPart = parts[0];
    if (integerPart.length > 8) return false;
    
    // Проверяем количество цифр после запятой
    if (parts[1] && parts[1].length > 4) return false;
    
    return true;
  };

  // Функция для форматирования площади (до 4 знаков после запятой)
  const formatPloshad = (value) => {
    if (!value || value === '') return '';
    const num = parseDecimal(value);
    if (isNaN(num)) return '';
    
    // Ограничиваем до 8 знаков до запятой
    if (num >= 100000000) {
      return '99999999.0000';
    }
    
    return num.toFixed(4);
  };

  // Обработчик изменения площади
  const handlePloshadChange = (id, value) => {
    const normalized = toDecimalPoint(value);
    if (validatePloshad(normalized)) {
      onUpdateField(id, 'srekme', normalized);
    }
  };

  // Обработчик потери фокуса для форматирования площади
  const handlePloshadBlur = (id, value) => {
    if (value && value !== '') {
      const formatted = formatPloshad(value);
      const numValue = parseFloat(formatted) || 0;
      
      // Получаем максимальную площадь выдела
      const maxPloshad = getMaxPloshad();
      
      if (maxPloshad > 0) {
        // Получаем сумму площадей других мероприятий (используем актуальные значения из recommendedMeasures)
        const totalOtherPloshad = getTotalPloshadExcluding(id);
        
        // Проверяем, что сумма площадей не превышает максимальную
        if ((totalOtherPloshad + numValue) > maxPloshad) {
          const vydelType = selectedForm45 === 4 
            ? (dataType === 'to' ? 'лесотаксационного' : 'лесопатологического')
            : (dataType === 'to' ? 'лесотаксационного' : 'лесопатологического');
          alert(`Ошибка: Сумма площадей мероприятий (${(totalOtherPloshad + numValue).toFixed(4)} га) не может превышать площадь ${vydelType} выдела (${maxPloshad.toFixed(4)} га).`);
          // Сбрасываем значение на максимально допустимое
          const maxAllowed = Math.max(0, maxPloshad - totalOtherPloshad);
          onUpdateField(id, 'srekme', maxAllowed > 0 ? maxAllowed.toFixed(4) : '');
          return;
        }
      }
      
      onUpdateField(id, 'srekme', formatted);
    }
  };

  return (
    <div className="group-section" style={{ marginTop: '30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h4>Рекомендуемые мероприятия</h4>
        <div>
          <button
            type="button"
            onClick={() => onAddRow(null)}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '5px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            + Добавить
          </button>
        </div>
      </div>
      
      <div style={{
        overflowX: 'auto',
        overflowY: 'visible',
        maxWidth: '100%',
        border: '1px solid #ddd',
        borderRadius: '6px'
      }}>
        <table className="table" style={{
          minWidth: '800px',
          width: '100%',
          marginBottom: 0
        }}>
          <thead>
            <tr>
              <th className="no-background" style={{ minWidth: '50px', width: '50px' }}>№</th>
              <th style={{ minWidth: '200px', width: '200px' }}>Вид</th>
              <th style={{ minWidth: '150px', width: '150px' }}>Сроки проведения</th>
              <th style={{ minWidth: '120px', width: '120px' }}>Площадь</th>
              <th style={{ minWidth: '200px', width: '200px' }}>Примечание</th>
              <th style={{ minWidth: '100px', width: '100px' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {recommendedMeasures.map((item, index) => {
              
              return (
              <tr key={item.id}>
                <td className="coordinate-cell no-background" style={{ minWidth: '50px', width: '50px', textAlign: 'center', verticalAlign: 'middle', padding: '8px' }}>
                  {index + 1}
                </td>
                <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px' }}>
                  <div 
                    className="reference-cell"
                    onClick={() => {
                      openPicker('rekvidmer', 25, item.id);
                    }}
                    style={{ 
                      minHeight: 60, 
                      height: 'auto',
                      cursor: 'pointer',
                      background: 'transparent',
                      position: 'relative'
                    }}
                  >
                    <textarea
                      readOnly
                      value={(() => {
                        if (item.rekvidmer) {
                          const handbook = handbooks.find(h => h.id === 25);
                          if (handbook && handbook.references) {
                            // Преобразуем item.rekvidmer в строку для правильного сравнения
                            const rekvidmerStr = String(item.rekvidmer);
                            const selectedOption = handbook.references.find(r => r.id.toString() === rekvidmerStr);
                            return selectedOption ? selectedOption.name : '';
                          }
                        }
                        return '';
                      })()}
                      placeholder="☰"
                      style={{
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        width: '100%',
                        height: '100%',
                        background: 'transparent',
                        color: item.rekvidmer ? '#495057' : '#6c757d',
                        fontStyle: item.rekvidmer ? 'normal' : 'italic'
                      }}
                      className="form-control coordinate-field"
                    />
                  </div>
                </td>
                <td className="coordinate-cell" style={{ minWidth: '150px', width: '150px' }}>
                  <input
                    type="text"
                    value={item.srokmzl || ''}
                    onChange={(e) => onUpdateField(item.id, 'srokmzl', e.target.value)}
                    className="form-control coordinate-field"
                    placeholder="Введите сроки"
                  />
                </td>
                <td className="coordinate-cell" style={{ minWidth: '120px', width: '120px' }}>
                  <DecimalInput
                    value={item.srekme || ''}
                    onChange={(e) => handlePloshadChange(item.id, e.target.value)}
                    onBlur={(e) => handlePloshadBlur(item.id, e.target.value)}
                    className="form-control coordinate-field"
                    placeholder="0.0000"
                    pattern="\d{0,8}([.,]\d{0,4})?"
                  />
                </td>
                <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px' }}>
                  <input
                    type="text"
                    value={item.primechmzl || ''}
                    onChange={(e) => onUpdateField(item.id, 'primechmzl', e.target.value)}
                    className="form-control coordinate-field"
                    placeholder="Введите примечание"
                  />
                </td>
                <td className="coordinate-cell" style={{ 
                  minWidth: '100px', 
                  width: '100px',
                  textAlign: 'center',
                  verticalAlign: 'middle'
                }}>
                  {recommendedMeasures.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onDeleteRow(item.id)}
                      style={{
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'inline-block'
                      }}
                    >
                      Удалить
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Picker модальное окно */}
      <PickerModal
        showPicker={showPicker}
        pickerSearch={pickerSearch}
        setPickerSearch={setPickerSearch}
        getFilteredOptions={getFilteredOptions}
        getSelectedIndexInFiltered={getSelectedIndexInFiltered}
        pickerValue={(() => {
          if (activeItemId) {
            const item = recommendedMeasures.find(m => m.id === activeItemId);
            const rawValue = item ? item[pickerField] : null;
            // Преобразуем значение в строку для правильного сравнения
            return rawValue !== null && rawValue !== undefined ? String(rawValue) : '';
          }
          return '';
        })()}
        selectPickerValue={selectPickerValue}
        closePicker={closePicker}
      />
    </div>
  );
};

export default RecommendedMeasuresSection;

